/**
 * Fixed bottom-right red toast that displays an error message with an icon and
 * close button. Auto-dismisses after 4s (with a 300ms slide-out animation) or
 * when the user clicks X; renders nothing when message is empty.
 * Rendered exclusively by ErrorContext's provider, which surfaces errors raised
 * anywhere in the app via the useError().showError API.
 */
import React, { useState, useEffect, useCallback } from "react";
import { X, AlertCircle } from "lucide-react";

interface ErrorToastProps {
    message: string;
    onClose: () => void;
}

const ErrorToast: React.FC<ErrorToastProps> = ({ message, onClose }) => {
    const [isExiting, setIsExiting] = useState(false);

    const handleClose = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => {
            onClose();
        }, 300);
    }, [onClose]);

    useEffect(() => {
        // Reset exiting state if the message changes (e.g., a new error appears)
        if (message) {
            setIsExiting(false);
            const timer = setTimeout(() => {
                handleClose();
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message, handleClose]);

    if (!message) return null;

    return (
        <div
            className={`fixed bottom-4 right-4 bg-red-700 text-white px-4 py-2 rounded-lg shadow-xl flex items-center justify-between z-50 ${
                isExiting ? "animate-slide-out-down" : "animate-slide-in-up"
            }`}
        >
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <span className="font-porsui text-sm flex-grow">{message}</span>
            <button
                onClick={handleClose}
                className="ml-8 p-1 rounded-full text-white hover:bg-red-800 focus:outline-none"
            >
                <X size={20} />
            </button>
        </div>
    );
};

export default ErrorToast;
